import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Loader2, Plus, Search, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import productApi from '@/lib/productApi';
import { confirmAction } from '@/lib/confirm';
import { toast } from '@/lib/toast';
import type { HubSpotUserOption, ReviewerGroup } from '@/types/product';

interface ReviewerGroupsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wordt aangeroepen zodra groepen zijn gewijzigd, zodat lijsten verversen. */
  onChanged: () => void;
}

const MAX_NAME_LENGTH = 60;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

/**
 * Beheer van keuzegroepen zoals "de CSM's". Eén groep, overal hetzelfde: een lid
 * dat je hier toevoegt telt meteen mee bij elk product dat de groep gebruikt.
 */
export default function ReviewerGroupsDialog({
  open,
  onOpenChange,
  onChanged,
}: ReviewerGroupsDialogProps) {
  const [groups, setGroups] = useState<ReviewerGroup[]>([]);
  const [users, setUsers] = useState<HubSpotUserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  /** Geopende groep; 'new' toont het formulier voor een nieuwe groep. */
  const [editing, setEditing] = useState<ReviewerGroup | 'new' | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftMembers, setDraftMembers] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextGroups, nextUsers] = await Promise.all([
        productApi.reviewerGroups(),
        productApi.hubspotUsers(),
      ]);
      setGroups(nextGroups);
      setUsers(nextUsers);
    } catch (error) {
      toast.error('Keuzegroepen konden niet worden geladen', { error });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setEditing(null);
    setSearch('');
    void load();
  }, [load, open]);

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('nl');
    if (!query) return users;
    return users.filter((user) =>
      `${user.name} ${user.email}`.toLocaleLowerCase('nl').includes(query),
    );
  }, [search, users]);

  const startNew = () => {
    setEditing('new');
    setDraftName('');
    setDraftMembers(new Set());
    setSearch('');
  };

  const startEdit = (group: ReviewerGroup) => {
    setEditing(group);
    setDraftName(group.name);
    setDraftMembers(new Set(group.members.map((member) => member.ownerId)));
    setSearch('');
  };

  const save = async () => {
    const name = draftName.trim();
    if (!name) {
      toast.error('Geef de groep een naam');
      return;
    }
    setBusy(true);
    try {
      const memberOwnerIds = [...draftMembers];
      if (editing === 'new') {
        await productApi.createReviewerGroup({ name, memberOwnerIds });
        toast.success(`Groep ${name} aangemaakt`);
      } else if (editing) {
        await productApi.updateReviewerGroup(editing.id, { name, memberOwnerIds });
        toast.success(`Groep ${name} bijgewerkt`);
      }
      setEditing(null);
      await load();
      onChanged();
    } catch (error) {
      toast.error('Groep kon niet worden opgeslagen', { error });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (group: ReviewerGroup) => {
    const warning = group.productCount
      ? `${group.productCount} ${group.productCount === 1 ? 'product gebruikt' : 'producten gebruiken'} deze groep en ${group.productCount === 1 ? 'verliest' : 'verliezen'} daarmee die reviewstap.`
      : undefined;
    if (
      !(await confirmAction({
        title: `${group.name} verwijderen?`,
        description: warning,
        confirmLabel: 'Verwijderen',
        destructive: true,
      }))
    )
      return;
    setBusy(true);
    try {
      await productApi.deleteReviewerGroup(group.id);
      toast.success(`Groep ${group.name} verwijderd`);
      await load();
      onChanged();
    } catch (error) {
      toast.error('Groep kon niet worden verwijderd', { error });
    } finally {
      setBusy(false);
    }
  };

  const editingGroup = editing === 'new' ? null : editing;

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="grid h-[min(760px,calc(100vh-2rem))] !w-[calc(100vw-2rem)] !max-w-[680px] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0">
        <DialogHeader className="min-w-0 border-b pb-4 pl-5 pr-12 pt-5 text-left sm:pb-5 sm:pl-6 sm:pr-12 sm:pt-6">
          <DialogTitle className="text-xl">
            {editing ? (editing === 'new' ? 'Nieuwe keuzegroep' : `Groep ${editingGroup?.name}`) : 'Keuzegroepen'}
          </DialogTitle>
          <DialogDescription className="max-w-[68ch] leading-5">
            {editing
              ? 'Uit deze groep beoordeelt per offerte één aangewezen persoon. Wie je hier toevoegt, telt meteen mee bij elk product dat de groep gebruikt.'
              : 'Groepen zoals "de CSM’s" beheer je hier één keer, en koppel je daarna aan producten. Een wijziging werkt direct door in alle gekoppelde producten.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 min-w-0 flex-col gap-3 px-5 py-4 sm:px-6">
          {loading ? (
            <div className="flex min-h-40 flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Laden…
            </div>
          ) : editing ? (
            <>
              <div className="shrink-0 space-y-1.5">
                <label className="text-xs font-semibold text-foreground" htmlFor="group-name">
                  Naam
                </label>
                <Input
                  id="group-name"
                  value={draftName}
                  maxLength={MAX_NAME_LENGTH}
                  onChange={(event) => setDraftName(event.target.value)}
                  placeholder="Bijvoorbeeld CSM"
                  className="h-10 bg-background"
                  disabled={busy}
                />
              </div>

              <div className="flex shrink-0 items-center justify-between gap-4 text-xs">
                <span className="font-semibold text-foreground">Leden</span>
                <span className="shrink-0 text-muted-foreground" aria-live="polite">
                  {draftMembers.size} gekozen
                </span>
              </div>

              <div className="relative shrink-0">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Zoek op naam of e-mailadres"
                  aria-label="Zoek HubSpot-gebruikers"
                  className="h-10 bg-background pl-9"
                  disabled={busy}
                />
              </div>

              <div className="scrollbar-thin min-h-40 min-w-0 flex-1 overflow-y-auto rounded-md border bg-background">
                {visibleUsers.length === 0 ? (
                  <div className="flex min-h-40 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
                    <Users className="h-5 w-5" />
                    Geen gebruikers gevonden.
                  </div>
                ) : (
                  visibleUsers.map((user) => {
                    const checked = draftMembers.has(user.ownerId);
                    return (
                      <label
                        key={user.ownerId}
                        className={`flex min-h-14 min-w-0 cursor-pointer items-center gap-3 border-b px-3 py-2.5 transition-colors last:border-b-0 hover:bg-muted/60 ${checked ? 'bg-[#EAF7FA]' : ''}`}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={busy}
                          onCheckedChange={() =>
                            setDraftMembers((current) => {
                              const next = new Set(current);
                              if (next.has(user.ownerId)) next.delete(user.ownerId);
                              else next.add(user.ownerId);
                              return next;
                            })
                          }
                        />
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E0F3FA] text-xs font-semibold text-[#006B7C]">
                          {initials(user.name)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{user.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {user.email}
                          </span>
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex shrink-0 items-center justify-between gap-2">
                <span className="text-xs font-semibold text-foreground">
                  {groups.length} {groups.length === 1 ? 'groep' : 'groepen'}
                </span>
                <Button size="sm" className="shrink-0" onClick={startNew} disabled={busy}>
                  <Plus className="mr-2 h-4 w-4" />
                  Nieuwe groep
                </Button>
              </div>

              <div className="scrollbar-thin min-h-48 min-w-0 flex-1 overflow-y-auto rounded-md border bg-background">
                {groups.length === 0 ? (
                  <div className="flex min-h-40 flex-col items-center justify-center gap-2 px-6 text-center text-sm text-muted-foreground">
                    <Users className="h-5 w-5" />
                    Nog geen keuzegroepen. Maak er één aan en koppel hem daarna aan producten.
                  </div>
                ) : (
                  groups.map((group) => (
                    <div
                      key={group.id}
                      className="flex min-h-16 min-w-0 items-center gap-3 border-b px-3 py-2.5 last:border-b-0"
                    >
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() => startEdit(group)}
                        disabled={busy}
                      >
                        <span className="block truncate text-sm font-medium">{group.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {group.members.length
                            ? `${group.members.length} ${group.members.length === 1 ? 'lid' : 'leden'}: ${group.members.map((member) => member.name).join(', ')}`
                            : 'Nog geen leden'}
                        </span>
                      </button>
                      <span className="shrink-0 rounded-full bg-secondary px-2 py-1 text-[11px] font-medium text-secondary-foreground">
                        {group.productCount} {group.productCount === 1 ? 'product' : 'producten'}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => startEdit(group)}
                        disabled={busy}
                      >
                        Bewerken
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="shrink-0 text-destructive hover:text-destructive"
                        aria-label={`Groep ${group.name} verwijderen`}
                        onClick={() => void remove(group)}
                        disabled={busy}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <p className="text-xs leading-5 text-muted-foreground">
                Een groep die je hier wijzigt, geldt meteen voor elk product dat eraan gekoppeld is.
                Je hoeft dus niet per product of per offerte opnieuw een groep samen te stellen.
              </p>
            </>
          )}
        </div>

        <DialogFooter className="min-w-0 gap-2 border-t bg-muted/30 px-5 py-4 sm:px-6 sm:space-x-0">
          {editing ? (
            <>
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => setEditing(null)}
                disabled={busy}
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Terug
              </Button>
              <Button className="w-full sm:w-auto" onClick={() => void save()} disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Groep opslaan
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              Sluiten
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
